import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RECURRENCE_CYCLES,
  RECURRENCE_CYCLE_MAP,
  computeTotals,
  type RecurrenceCycle,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { money, plural, WORDS } from '../../lib/format';
import { useDocumentDefaults } from '../../lib/settings';
import { Icon } from '../../components/Icon';
import { ErrorState, PageHead } from '../../components/ui';

interface SupplierOption {
  id: string;
  name: string;
  paymentTermDays: number;
}

interface LedgerOption {
  id: string;
  code: string;
  name: string;
}

interface DraftLine {
  key: string;
  accountId: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

let lineKey = 0;
const emptyLine = (): DraftLine => ({
  key: `rb${lineKey++}`,
  accountId: '',
  description: '',
  quantity: '1',
  unitPrice: '',
});

const validOf = (lines: DraftLine[]) =>
  lines.filter((l) => l.accountId && l.description.trim());

/**
 * إنشاء اشتراك مورد دوري وتعديله.
 *
 * البنود كبنود فاتورة المورد: كل بند يُحمَّل على حساب مصروف أو أصل في الدليل،
 * لأن هذا ما يُرحَّل حين تُستلم الفاتورة. والجدولة كاشتراكات العملاء حرفًا
 * بحرف — الدورة ويوم الإصدار وتاريخ الانتهاء — مع حدٍّ اختياري بعدد الفواتير.
 */
export function RecurringBillEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const defaults = useDocumentDefaults();

  const [supplierId, setSupplierId] = useState(params.get('supplierId') ?? '');
  const [title, setTitle] = useState('');
  const [cycle, setCycle] = useState<RecurrenceCycle>('MONTHLY');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endMode, setEndMode] = useState<'open' | 'date' | 'count'>('open');
  const [endDate, setEndDate] = useState('');
  const [maxRuns, setMaxRuns] = useState('');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [dueDays, setDueDays] = useState('');
  const [autoReceive, setAutoReceive] = useState(false);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const [discount, setDiscount] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [loaded, setLoaded] = useState(false);

  const suppliers = useQuery({
    queryKey: ['suppliers', 'options'],
    queryFn: () =>
      api<{ items: SupplierOption[] }>('/finance/suppliers?pageSize=100&isActive=true'),
  });
  const accounts = useQuery({
    queryKey: ['ledger', 'postable'],
    queryFn: () => api<LedgerOption[]>('/accounting/accounts/postable'),
  });

  const existing = useQuery({
    queryKey: ['recurring-bills', id],
    queryFn: () => api<Record<string, any>>(`/finance/recurring-bills/${id}`),
    enabled: editing,
  });

  /* نسبة الضريبة الافتراضية من إعدادات الشركة، وتُطبَّق على الجديد وحده. */
  useEffect(() => {
    if (editing || loaded || !defaults.ready) return;
    setTaxRate(String(defaults.settings.taxRate));
    setLoaded(true);
  }, [defaults, editing, loaded]);

  useEffect(() => {
    const r = existing.data;
    if (!r || loaded) return;
    setSupplierId(r.supplierId);
    setTitle(r.title);
    setCycle(r.cycle);
    setStartDate(r.startDate);
    setEndDate(r.endDate ?? '');
    setMaxRuns(r.maxRuns ? String(r.maxRuns) : '');
    setEndMode(r.endDate ? 'date' : r.maxRuns ? 'count' : 'open');
    setDayOfMonth(String(r.dayOfMonth));
    setDueDays(r.dueDays === null ? '' : String(r.dueDays));
    setAutoReceive(r.autoReceive);
    setNotes(r.notes ?? '');
    setDiscount(String(Number(r.discount) || ''));
    setTaxRate(String(Number(r.taxRate)));
    setLines(
      (r.lines ?? []).map((l: any) => ({
        key: `rb${lineKey++}`,
        accountId: l.accountId,
        description: l.description,
        quantity: String(Number(l.quantity)),
        unitPrice: String(Number(l.unitPrice)),
      })),
    );
    setLoaded(true);
  }, [existing.data, loaded]);

  const valid = validOf(lines);
  const totals = computeTotals(
    valid.map((l) => ({ quantity: Number(l.quantity) || 0, unitPrice: Number(l.unitPrice) || 0 })),
    { headerDiscount: Number(discount) || 0, taxRate: Number(taxRate) || 0 },
  );

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title: title.trim(),
        notes: notes.trim() || null,
        endDate: endMode === 'date' && endDate ? endDate : null,
        maxRuns: endMode === 'count' && Number(maxRuns) > 0 ? Number(maxRuns) : null,
        dayOfMonth: Number(dayOfMonth) || 1,
        discount: Number(discount) || 0,
        taxRate: Number(taxRate) || 0,
        dueDays: dueDays.trim() === '' ? null : Number(dueDays) || 0,
        autoReceive,
        lines: valid.map((l) => ({
          accountId: l.accountId,
          description: l.description.trim(),
          quantity: Number(l.quantity) || 1,
          unitPrice: Number(l.unitPrice) || 0,
        })),
      };

      return editing
        ? api<{ id: string }>(`/finance/recurring-bills/${id}`, { method: 'PATCH', body: payload })
        : api<{ id: string }>('/finance/recurring-bills', {
            method: 'POST',
            body: { ...payload, supplierId, cycle, startDate },
          });
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['recurring-bills'] });
      navigate(`/finance/recurring-bills/${saved.id}`);
    },
  });

  if (existing.isError) {
    return <ErrorState error={existing.error} onRetry={() => existing.refetch()} />;
  }

  const patchLine = (key: string, changes: Partial<DraftLine>) =>
    setLines((list) => list.map((l) => (l.key === key ? { ...l, ...changes } : l)));

  const selectedSupplier = suppliers.data?.items.find((s) => s.id === supplierId);
  const canSave = title.trim().length >= 2 && (editing || supplierId) && valid.length > 0;

  return (
    <div className="page">
      <PageHead
        title={editing ? 'تعديل اشتراك مورد' : 'اشتراك مورد دوري جديد'}
        subtitle="قالب فاتورة مورد تُسجَّل نفسها في كل دورة"
        actions={
          <>
            <Link to="/finance/recurring-bills" className="btn">
              رجوع
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => save.mutate()}
              disabled={!canSave || save.isPending}
            >
              {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
            </button>
          </>
        }
      />

      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">الاشتراك</h2>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <div className="field span-2">
              <label className="label req" htmlFor="rb-title">
                عنوان الاشتراك
              </label>
              <input
                id="rb-title"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="إيجار المكتب"
              />
              <span className="tiny mute">
                يُكتب في ملاحظات كل فاتورة مع شهرها — مثل "إيجار المكتب — يناير ٢٠٢٦".
              </span>
            </div>

            <div className="field span-2">
              <label className="label req" htmlFor="rb-supplier">
                المورد
              </label>
              <select
                id="rb-supplier"
                className="select"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                disabled={editing}
              >
                <option value="">اختر موردًا</option>
                {suppliers.data?.items.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              {editing ? (
                /* المورد لا يتغيّر: الفواتير المسجّلة منسوبة إليه وذممه محسوبة منها. */
                <span className="tiny mute">
                  المورد لا يُغيَّر بعد الإنشاء. أنشئ اشتراكًا جديدًا لمورد آخر.
                </span>
              ) : (
                selectedSupplier && (
                  <span className="tiny mute">
                    مهلة السداد المتفق عليها{' '}
                    {plural(selectedSupplier.paymentTermDays, WORDS.day)}.
                  </span>
                )
              )}
            </div>

            <div className="field">
              <label className="label" htmlFor="rb-cycle">
                الدورة
              </label>
              <select
                id="rb-cycle"
                className="select"
                value={cycle}
                onChange={(e) => setCycle(e.target.value as RecurrenceCycle)}
                disabled={editing}
              >
                {RECURRENCE_CYCLES.map((c) => (
                  <option key={c} value={c}>
                    {RECURRENCE_CYCLE_MAP[c].label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="label req" htmlFor="rb-start">
                تاريخ البدء
              </label>
              <input
                id="rb-start"
                className="input num"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={editing}
              />
              {editing && <span className="tiny mute">لا يُغيَّر بعد الإنشاء.</span>}
            </div>

            <div className="field">
              <label className="label" htmlFor="rb-day">
                يوم الإصدار من الشهر
              </label>
              <input
                id="rb-day"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value)}
              />
              {Number(dayOfMonth) > 28 && (
                <span className="tiny mute">في الشهور الأقصر تُسجَّل في آخر يوم منها.</span>
              )}
            </div>

            <div className="field">
              <label className="label" htmlFor="rb-due">
                مهلة السداد بالأيام
              </label>
              <input
                id="rb-due"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                value={dueDays}
                placeholder={selectedSupplier ? String(selectedSupplier.paymentTermDays) : ''}
                onChange={(e) => setDueDays(e.target.value)}
              />
              <span className="tiny mute">
                تاريخ استحقاق الفاتورة = تاريخها + المهلة. اتركها فارغة لتُؤخذ من مهلة المورد.
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="rb-endmode">
                ينتهي
              </label>
              <select
                id="rb-endmode"
                className="select"
                value={endMode}
                onChange={(e) => setEndMode(e.target.value as typeof endMode)}
              >
                <option value="open">مفتوح بلا نهاية</option>
                <option value="date">في تاريخ معيّن</option>
                <option value="count">بعد عدد من الفواتير</option>
              </select>
            </div>

            {endMode === 'date' && (
              <div className="field">
                <label className="label" htmlFor="rb-end">
                  تاريخ الانتهاء
                </label>
                <input
                  id="rb-end"
                  className="input num"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            )}

            {endMode === 'count' && (
              <div className="field">
                <label className="label" htmlFor="rb-count">
                  عدد الفواتير
                </label>
                <input
                  id="rb-count"
                  className="input num"
                  dir="ltr"
                  inputMode="numeric"
                  value={maxRuns}
                  onChange={(e) => setMaxRuns(e.target.value)}
                />
                <span className="tiny mute">عقد إيجار سنة بدفعات شهرية = ١٢.</span>
              </div>
            )}

            <div className="field span-2">
              <label className="check">
                <input
                  type="checkbox"
                  checked={autoReceive}
                  onChange={(e) => setAutoReceive(e.target.checked)}
                />
                <span>استلام تلقائي — تُسجَّل الفاتورة مستلمة وتُرحَّل إلى الدفاتر فورًا</span>
              </label>
              {/*
                الافتراضي مسودة عن قصد: الاستلام قيد في الدفتر العام، ومبلغ
                المورد كثيرًا ما يتغيّر من شهر لآخر. المسودة تُعدَّل قبل
                استلامها، أما القيد الخاطئ فلا يُصحَّح إلا بالإلغاء والعكس.
              */}
              <span className={`tiny ${autoReceive ? 'neg' : 'mute'}`}>
                {autoReceive
                  ? 'الفواتير ستُرحَّل بلا مراجعة. مناسب للمبالغ الثابتة كالإيجار.'
                  : 'الفواتير تُنشأ مسودات ليراجع أحد المبلغ ثم يستلمها من فواتير الموردين.'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">بنود الفاتورة</h2>
        </div>
        <div className="card-body">
          <div className="table-wrap">
            <table className="table lines-table">
              <thead>
                <tr>
                  <th style={{ width: '32%' }}>الحساب</th>
                  <th>البيان</th>
                  <th style={{ width: 90 }}>الكمية</th>
                  <th style={{ width: 120 }}>السعر</th>
                  <th style={{ width: 120 }}>الإجمالي</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={line.key}>
                    <td>
                      <select
                        className="select"
                        value={line.accountId}
                        aria-label={`حساب البند ${index + 1}`}
                        onChange={(e) => patchLine(line.key, { accountId: e.target.value })}
                      >
                        <option value="">اختر حسابًا</option>
                        {accounts.data?.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} — {a.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="input"
                        value={line.description}
                        aria-label={`بيان البند ${index + 1}`}
                        onChange={(e) => patchLine(line.key, { description: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="input num"
                        type="number"
                        min="0"
                        step="any"
                        aria-label={`كمية البند ${index + 1}`}
                        value={line.quantity}
                        onChange={(e) => patchLine(line.key, { quantity: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="input num"
                        type="number"
                        min="0"
                        step="any"
                        aria-label={`سعر البند ${index + 1}`}
                        value={line.unitPrice}
                        onChange={(e) => patchLine(line.key, { unitPrice: e.target.value })}
                      />
                    </td>
                    <td className="num strong">
                      {money((Number(line.quantity) || 0) * (Number(line.unitPrice) || 0))}
                    </td>
                    <td className="end">
                      <button
                        className="btn btn-sm btn-icon btn-ghost"
                        type="button"
                        aria-label={`حذف البند ${index + 1}`}
                        disabled={lines.length === 1}
                        onClick={() => setLines((l) => l.filter((r) => r.key !== line.key))}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            className="btn btn-sm mt-2"
            type="button"
            onClick={() => setLines((l) => [...l, emptyLine()])}
          >
            <Icon name="plus" size={14} />
            إضافة بند
          </button>

          <div className="totals-box mt-4">
            <div className="totals-row">
              <span>المجموع</span>
              <span className="num">{money(totals.subtotal)}</span>
            </div>
            <div className="totals-row">
              <label htmlFor="rb-discount">خصم</label>
              <input
                id="rb-discount"
                className="input num"
                type="number"
                min="0"
                step="any"
                style={{ width: 120 }}
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
              />
            </div>
            <div className="totals-row">
              <label htmlFor="rb-tax">
                الضريبة
                <span className="tiny mute"> (نسبة مئوية)</span>
              </label>
              <div className="row" style={{ gap: 6 }}>
                <input
                  id="rb-tax"
                  className="input num"
                  type="number"
                  min="0"
                  max="100"
                  step="any"
                  style={{ width: 70 }}
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                />
                <span className="num mute">{money(totals.taxAmount)}</span>
              </div>
            </div>
            <div className="totals-row totals-final">
              <span>قيمة الدورة</span>
              <span className="num">{money(totals.total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">ملاحظات</h2>
        </div>
        <div className="card-body">
          <div className="field">
            <label className="label" htmlFor="rb-notes">
              ملاحظات تُنسخ إلى كل فاتورة
            </label>
            <textarea
              id="rb-notes"
              className="input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
